import React, { Suspense, lazy, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import './App.css';
import './ComponentsStyling/ScrollAnimation.css';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';

const SITE_URL = 'https://ieeecis-uj.org';

const ROUTE_META = {
  '/': {
    title: 'IEEE CIS UJ | Official Student Chapter Website',
    description: 'Official website of the IEEE Computational Intelligence Society Student Branch at the University of Jordan featuring chapter news, events, teams, resources, and membership information.',
  },
  '/partners': {
    title: 'Partners | IEEE CIS UJ',
    description: 'Explore the strategic partners, advisors, and external collaborators supporting IEEE CIS UJ initiatives, events, and student development programs.',
  },
  '/teams': {
    title: 'Teams | IEEE CIS UJ',
    description: 'Meet the leadership team, technical divisions, stars of the month, and chapter members behind IEEE CIS UJ.',
  },
  '/events': {
    title: 'Events | IEEE CIS UJ',
    description: 'Browse featured IEEE CIS UJ workshops, technical sessions, competitions, and chapter events across AI and computational intelligence topics.',
  },
  '/all-events': {
    title: 'Event Archive | IEEE CIS UJ',
    description: 'View the complete archive of IEEE CIS UJ events, workshops, and community activities with historical details and descriptions.',
  },
  '/join': {
    title: 'Join IEEE CIS UJ',
    description: 'Start your IEEE and IEEE CIS UJ membership journey with official registration guidance, onboarding steps, and chapter contact details.',
  },
  '/faq': {
    title: 'FAQ | IEEE CIS UJ',
    description: 'Find answers to common questions about IEEE CIS, IEEE CIS UJ membership, events, benefits, and participation.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | IEEE CIS UJ',
    description: 'Review the IEEE CIS UJ privacy policy covering data collection, usage, storage, deletion requests, and contact information.',
  },
  '/store': {
    title: 'Official Store | IEEE CIS UJ',
    description: 'Browse and order official IEEE CIS UJ merchandise, apparel, and accessories from the chapter store.',
  },
  '*': {
    title: 'Page Not Found | IEEE CIS UJ',
    description: 'The requested IEEE CIS UJ page could not be found. Return to the home page or browse the chapter navigation to continue.',
  },
};

const updateHeadTag = (selector, value) => {
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute('content', value);
  }
};

const updateCanonical = href => {
  const canonicalLink = document.head.querySelector('link[rel="canonical"]');
  if (canonicalLink) {
    canonicalLink.setAttribute('href', href);
  }
};

function RouteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = ROUTE_META[pathname] || ROUTE_META['*'];
    const canonicalPath = pathname === '/' ? '/' : `/#${pathname}`;

    document.title = meta.title;
    updateHeadTag('meta[name="description"]', meta.description);
    updateHeadTag('meta[property="og:title"]', meta.title);
    updateHeadTag('meta[property="og:description"]', meta.description);
    updateHeadTag('meta[name="twitter:title"]', meta.title);
    updateHeadTag('meta[name="twitter:description"]', meta.description);
    updateCanonical(`${SITE_URL}${canonicalPath}`);
  }, [pathname]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const HomePage = lazy(() => import('./pages/HomePage'));
const YouTubePage = lazy(() => import('./pages/YouTubePage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const JoinPage = lazy(() => import('./pages/JoinPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const AllEvents = lazy(() => import('./components/AllEvents'));
const FAQ = lazy(() => import('./components/FAQ'));
const StorePage = lazy(() => import('./pages/StorePage'));

const RouteFallback = () => <div className="route-fallback" aria-hidden="true" />;

const NotFoundPage = ({ isDarkMode }) => (
  <>
    <Header isDarkMode={isDarkMode} />
    <main className="not-found-page" aria-labelledby="not-found-title">
      <div className="not-found-card">
        <p className="not-found-card__eyebrow">IEEE CIS UJ · Navigation Error</p>
        <span className="not-found-card__ghost" aria-hidden="true">404</span>
        <h1 id="not-found-title" className="not-found-card__title">Page Not Found</h1>
        <p className="not-found-card__text">
          The page you requested is unavailable or has moved. Use the primary navigation or return to the main chapter portal.
        </p>
        <div className="not-found-card__actions">
          <Link to="/" className="not-found-card__button not-found-card__button--primary">Back to Home</Link>
          <Link to="/events" className="not-found-card__button not-found-card__button--ghost">Browse Events</Link>
        </div>
      </div>
    </main>
    <Footer isDarkMode={isDarkMode} />
  </>
);

/**
 * Core application shell. Establishes the client-side route map and forwards
 * the active theme state to every page layout via props.
 */
function AppContent() {
  const [isDarkMode] = React.useState(true);

  const AllEventsPage = () => (
    <>
      <Header isDarkMode={isDarkMode} />
      <main className="page-main" aria-label="All events page content">
      <AllEvents />
      </main>
      <Footer isDarkMode={isDarkMode} />
    </>
  );

  return (
    <div className={isDarkMode ? 'dark-mode' : 'light-mode'}>
      <Suspense fallback={<RouteFallback />}>
        <ScrollToTop />
        <RouteMetadata />
        <Routes>
          <Route path="/" element={<HomePage isDarkMode={isDarkMode} />} />
          <Route path="/partners" element={<PartnersPage isDarkMode={isDarkMode} />} />
          <Route path="/teams" element={<TeamsPage isDarkMode={isDarkMode} />} />
          <Route path="/events" element={<EventsPage isDarkMode={isDarkMode} />} />
          <Route path="/join" element={<JoinPage isDarkMode={isDarkMode} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage isDarkMode={isDarkMode} />} />
          <Route path="/all-events" element={<AllEventsPage />} />
          <Route path="/faq" element={<FAQ isDarkMode={isDarkMode} />} />
          <Route path="/youtube" element={<YouTubePage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="*" element={<NotFoundPage isDarkMode={isDarkMode} />} />
        </Routes>
      </Suspense>
      <CookieConsent />
    </div>
  );
}

/**
 * Root component. Wraps the application tree in HashRouter to enable
 * client-side navigation without requiring server-side URL rewriting.
 */
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;